import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import { BarChart3, Users, Home, Building2, TrendingUp, Award, Store } from 'lucide-react';

interface ImpactSummary {
  enabled: boolean;
  allTime?: {
    totalWorkHours?: number;
    totalServiceCount?: number;
    totalUniqueCustomers?: number;
    activeFamilies?: number;
    estatesCovered?: number;
    senYouthParticipants?: number;
  };
  month?: {
    totalWorkHours?: number;
    totalServiceCount?: number;
    totalUniqueCustomers?: number;
  };
  monthWorkHours?: number;
}

interface DailyData {
  date: string;
  workHours: number;
  serviceCount: number;
  uniqueCustomers: number;
  senYouthParticipants: number;
}

interface LeaderboardItem {
  rank: number;
  merchantCode: string;
  merchantName: string;
  totalHours: number;
  totalService: number;
  totalUnique: number;
  totalSenYouth: number;
}

interface EstateItem {
  estate: string;
  totalOrders: number;
  uniqueCustomers: number;
  totalRevenue: number;
}

const AdminImpactDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const token = localStorage.getItem('admin_token');

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [summary, setSummary] = useState<ImpactSummary | null>(null);
  const [daily, setDaily] = useState<DailyData[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardItem[]>([]);
  const [estates, setEstates] = useState<EstateItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const currentMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    // 注意：can 函數每次 render 都會重建，不可放入 dependency array，
    // 否則會導致無限循環觸發 API 請求（ERR_INSUFFICIENT_RESOURCES）。
    const hasImpactPermission = can(PERMISSIONS.IMPACT_READ);
    if (!hasImpactPermission) {
      setError('沒有權限查看影響力儀表板');
      setLoading(false);
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configRes, summaryRes, dailyRes, leaderboardRes, estatesRes] = await Promise.all([
        apiFetch('/api/public/admin/impact/config'),
        apiFetch('/api/public/admin/impact/summary'),
        apiFetch('/api/public/admin/impact/daily?days=30'),
        apiFetch(`/api/public/admin/impact/leaderboard?month=${currentMonth}`),
        apiFetch('/api/public/admin/impact/estates'),
      ]);

      if (configRes.status === 401 || configRes.status === 403) {
        setError('權限不足或登入已過期，請重新登入後再試。');
        setLoading(false);
        return;
      }

      const configData = await configRes.json().catch(() => ({}));
      if (!configRes.ok || configData.enabled === false) {
        setEnabled(false);
        setLoading(false);
        return;
      }

      setEnabled(true);
      setSummary(await summaryRes.json().catch(() => null));
      const dailyData = await dailyRes.json().catch(() => ({}));
      setDaily(Array.isArray(dailyData.data) ? dailyData.data : []);
      const lbData = await leaderboardRes.json().catch(() => ({}));
      setLeaderboard(Array.isArray(lbData.data) ? lbData.data : []);
      const estData = await estatesRes.json().catch(() => ({}));
      setEstates(Array.isArray(estData.data) ? estData.data : []);
    } catch (e: any) {
      setError(e.message || '載入失敗，請檢查網絡連線或重新整理頁面。');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="impact" onLogout={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin'); }}>
        <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">載入中...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout currentPage="impact" onLogout={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin'); }}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          <p className="font-bold">{error}</p>
          <p className="text-sm mt-2">若問題持續，請先登出再重新登入，以更新權限資料。</p>
          <button
            onClick={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin/login'); }}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-bold"
          >
            重新登入
          </button>
        </div>
      </AdminLayout>
    );
  }

  if (!enabled) {
    return (
      <AdminLayout currentPage="impact" onLogout={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin'); }}>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-6 py-8 rounded-lg text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
          <p className="text-lg font-bold">影響力儀表板未啟用</p>
          <p className="text-sm mt-2">請在系統設置中將 impact_dashboard.admin_enabled 設為 1</p>
        </div>
      </AdminLayout>
    );
  }

  const maxHours = Math.max(...daily.map(d => d.workHours), 1);
  const maxService = Math.max(...daily.map(d => d.serviceCount), 1);

  const kpiCards = [
    { label: '累計工作小時', value: (summary?.allTime?.totalWorkHours || 0).toFixed(1), icon: BarChart3, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
    { label: '累計服務人次', value: summary?.allTime?.totalServiceCount || 0, icon: TrendingUp, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
    { label: '獨立服務客戶', value: summary?.allTime?.totalUniqueCustomers || 0, icon: Users, color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' },
    { label: '參與家庭', value: summary?.allTime?.activeFamilies || 0, icon: Home, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' },
    { label: '屋苑覆蓋', value: summary?.allTime?.estatesCovered || 0, icon: Building2, color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
    { label: '本月工作小時', value: (summary?.monthWorkHours || 0).toFixed(1), icon: Award, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  ];

  return (
    <AdminLayout currentPage="impact" onLogout={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin'); }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-orange-600" />
          社區影響力儀表板
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">鄰里商家與社區服務影響力總覽</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {kpiCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${card.color}`}>
                <Icon size={20} />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* 30-Day Trend */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">30 日趨勢</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Work Hours Chart */}
          <div>
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3">每日工作小時</p>
            <div className="flex items-end gap-1 h-40">
              {daily.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-orange-500 rounded-t transition-all hover:bg-orange-600"
                    style={{ height: `${Math.max((d.workHours / maxHours) * 100, 2)}%` }}
                    title={`${d.date}: ${d.workHours.toFixed(1)} 小時`}
                  />
                  {i % 5 === 0 && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 rotate-45 origin-top-left whitespace-nowrap">
                      {d.date.slice(5)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Service Count Chart */}
          <div>
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3">每日服務人次</p>
            <div className="flex items-end gap-1 h-40">
              {daily.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                    style={{ height: `${Math.max((d.serviceCount / maxService) * 100, 2)}%` }}
                    title={`${d.date}: ${d.serviceCount} 人次`}
                  />
                  {i % 5 === 0 && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 rotate-45 origin-top-left whitespace-nowrap">
                      {d.date.slice(5)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <Store size={20} />
          鄰里商家排行榜（{currentMonth}）
        </h2>
        {leaderboard.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">本月暫無數據</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                <tr>
                  <th className="p-3">排名</th>
                  <th className="p-3">商家</th>
                  <th className="p-3">代碼</th>
                  <th className="p-3">工作小時</th>
                  <th className="p-3">服務人次</th>
                  <th className="p-3">獨立客戶</th>
                  <th className="p-3">SEN 青年</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {leaderboard.map((item) => (
                  <tr key={item.merchantCode} className="hover:bg-orange-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="p-3 font-bold text-gray-800 dark:text-white">
                      {item.rank <= 3 ? (
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs ${
                          item.rank === 1 ? 'bg-yellow-500' : item.rank === 2 ? 'bg-gray-400' : 'bg-amber-600'
                        }`}>{item.rank}</span>
                      ) : item.rank}
                    </td>
                    <td className="p-3 font-semibold text-gray-800 dark:text-white">{item.merchantName}</td>
                    <td className="p-3 font-mono text-gray-600 dark:text-gray-300">{item.merchantCode}</td>
                    <td className="p-3 text-gray-800 dark:text-white">{item.totalHours.toFixed(1)}</td>
                    <td className="p-3 text-gray-800 dark:text-white">{item.totalService}</td>
                    <td className="p-3 text-gray-800 dark:text-white">{item.totalUnique}</td>
                    <td className="p-3 text-gray-800 dark:text-white">{item.totalSenYouth}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Estates Coverage */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <Building2 size={20} />
          屋苑覆蓋表
        </h2>
        {estates.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">暫無數據</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                <tr>
                  <th className="p-3">屋苑</th>
                  <th className="p-3">完成訂單</th>
                  <th className="p-3">獨立客戶</th>
                  <th className="p-3">總營業額</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {estates.map((item) => (
                  <tr key={item.estate} className="hover:bg-orange-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="p-3 font-semibold text-gray-800 dark:text-white">{item.estate}</td>
                    <td className="p-3 text-gray-800 dark:text-white">{item.totalOrders}</td>
                    <td className="p-3 text-gray-800 dark:text-white">{item.uniqueCustomers}</td>
                    <td className="p-3 text-gray-800 dark:text-white">HK${item.totalRevenue.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminImpactDashboard;
